import { SearchIcon } from '@chakra-ui/icons'
import {
  Flex,
  Heading,
  Button,
  InputGroup,
  InputLeftElement,
  Input,
  Box,
  Table,
  Thead,
  Tr,
  Tbody,
  Td,
  HStack,
  Badge,
  Text,
  Menu,
  MenuButton,
  MenuList,
  Checkbox,
  VStack,
} from '@chakra-ui/react'
import { ChevronDownIcon } from '@chakra-ui/icons'
import React, { useState, useEffect } from 'react'
import { useGetUsersByTenantsQuery, useUpdateUserLocationsMutation } from '../../api/api'
import { useUser } from '../../../../context/UserContext'
import { useGetAllLocationsQuery } from '../../../reports/api/reportsApi'
import InviteUserModal from './modals/InviteUserModal'
import { toast } from 'react-toastify'
import UserMenu from '../../../../app/components/UserMenu'
import { HeaderText, RowText } from '../utils'

export function UserManagement() {
  const [search, setSearch] = useState('')
  const { user } = useUser()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tempUserLocations, setTempUserLocations] = useState({})
  const { data: users, isLoading: isUsersLoading } = useGetUsersByTenantsQuery({
    tenant_id: user.tenant.id,
  })
  const { data: locations, isLoading: isLocationsLoading } = useGetAllLocationsQuery(
    {},
    { skip: !user },
  )
  const [updateUserLocations] = useUpdateUserLocationsMutation()

  const [userLocations, setUserLocations] = useState({})
  const [locationSearch, setLocationSearch] = useState('')

  useEffect(() => {
    if (users?.results) {
      const initialMap = {}
      users.results.forEach((u) => {
        initialMap[u.id] = u.location_ids || []
      })
      setUserLocations(initialMap)
      setTempUserLocations(initialMap)
    }
  }, [users])

  const toggleTempUserLocation = (userId, locationId) => {
    setTempUserLocations((prev) => {
      const current = prev[userId] || []
      const exists = current.includes(locationId)
      const updated = exists
        ? current.filter((id) => id !== locationId)
        : [...current, locationId]
      return { ...prev, [userId]: updated }
    })
  }

  const mappedUsers = (users?.results || []).map((u) => ({
    id: u.id,
    name: u.name,
    email: u.email,
    locationIds: userLocations[u.id] || [],
    status: u.last_login ? 'Active' : 'Pending',
  }))

  const filteredUsers = mappedUsers.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  )

  const arraysEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((val, index) => val === sortedB[index])
  }

  const handleUpdate = async (userId: number) => {
    const toastId = toast.info('Updating...', {
      style: { padding: '10px 20px' },
      autoClose: false,
      isLoading: true,
      progress: undefined,
    })
    try {
      await updateUserLocations({
        tenant_id: user.tenant.id,
        user_id: userId,
        location_ids: tempUserLocations[userId],
      }).unwrap()
      toast.update(toastId, {
        render: 'Locations updated successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
    } catch (error) {
      toast.update(toastId, {
        render: `Failed to update locations`,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  if (isUsersLoading || isLocationsLoading) {
    return <Text>Loading users...</Text>
  }

  return (
    <>
      <InviteUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(!isModalOpen)}
        locations={locations}
        user={user}
      />
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md">Manage users and permissions</Heading>
        <Box display="flex" alignItems="center" gap={4}>
          <Button
            width="200px"
            colorScheme="button.primary"
            onClick={() => setIsModalOpen(true)}
          >
            Invite User
          </Button>
          <UserMenu />
        </Box>
      </Flex>

      <InputGroup mb={6} maxW="400px">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search"
          rounded="full"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </InputGroup>

      <Box overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <HeaderText>User</HeaderText>
              <HeaderText>Location/s</HeaderText>
            </Tr>
          </Thead>
          <Tbody>
            {filteredUsers.map((u) => {
              const selectedLocations = locations.results.filter((loc) =>
                tempUserLocations[u.id]?.includes(loc.id),
              )

              const displayNames = selectedLocations.map((loc) => loc.title)
              let displayLabel =
                selectedLocations.length === 0
                  ? 'Select location(s)'
                  : selectedLocations.length === 1
                  ? selectedLocations[0].title
                  : `${selectedLocations.length} locations`

              const hasChanges = !arraysEqual(
                userLocations[u.id],
                tempUserLocations[u.id],
              )

              return (
                <Tr key={u.id}>
                  <Td>
                    <RowText>{u.name}</RowText>
                    <HStack spacing={2} mt={1}>
                      <Text fontSize="13px" color="#9B9B9B">
                        {u.email}
                      </Text>

                      {u.status === 'Pending' && (
                        <Badge colorScheme="orange" fontSize="0.7em">
                          Pending
                        </Badge>
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        variant="ghost"
                        rightIcon={<ChevronDownIcon />}
                        size="sm"
                        fontWeight="normal"
                        w="fit-content"
                        color="#484848"
                      >
                        {displayLabel}
                      </MenuButton>
                      <MenuList p={0} minW="250px" maxH="300px" overflowY="auto">
                        <Box p={3} borderBottom="1px solid" borderColor="gray.100">
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                              placeholder="Search location"
                              size="sm"
                              value={locationSearch}
                              onChange={(e) => setLocationSearch(e.target.value)}
                            />
                          </InputGroup>
                          {selectedLocations.length > 0 && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              Selected: {displayNames.join(', ')}
                            </Text>
                          )}
                        </Box>
                        <Box p={2}>
                          <VStack align="start" spacing={1}>
                            {locations?.results
                              ?.filter((loc) =>
                                loc.title
                                  .toLowerCase()
                                  .includes(locationSearch.toLowerCase()),
                              )
                              .map((loc) => (
                                <Checkbox
                                  key={loc.id}
                                  isChecked={tempUserLocations[u.id]?.includes(loc.id)}
                                  onChange={() => toggleTempUserLocation(u.id, loc.id)}
                                  w="full"
                                >
                                  {loc.title}
                                </Checkbox>
                              ))}
                          </VStack>

                          {hasChanges && (
                            <Button
                              size="sm"
                              mt={3}
                              colorScheme="blue"
                              w="full"
                              onClick={() => handleUpdate(u.id)}
                            >
                              Update
                            </Button>
                          )}
                        </Box>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  )
}
